import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, ChartLine, LockKeyhole, ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import Logo from "../components/Logo";

const restaurants = [
  { slug: "gilded-fork", name: "The Gilded Fork", cuisine: "Modern European", image: "https://images.unsplash.com/photo-1663530761401-15eefb544889" },
  { slug: "olive-ember", name: "Olive & Ember", cuisine: "Mediterranean Fire", image: "https://images.unsplash.com/photo-1625944525533-473f1a3d54e7?w=1600&q=80" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white relative overflow-hidden">
      {/* glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(229,169,60,0.25), transparent)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(255,75,62,0.18), transparent)" }} />

      {/* Nav */}
      <header className="relative z-20 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2" data-testid="brand">
          <Logo size={36} />
          <span className="font-display text-2xl tracking-tight">Mnu<span className="gold-text">Verse</span></span>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 px-6 md:px-12 pt-8 md:pt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70 font-mono uppercase tracking-[0.2em]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E5A93C]" /> AI-Driven Technology
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6">
              Your paper menu <span className="italic gold-text">just retired.</span>
            </h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E5A93C] mt-4" data-testid="brand-caption">AI-powered visual + conversational menu</p>
            <p className="mt-6 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              MnuVerse replaces static menus with a cinematic, video-first ordering experience — engineered to raise check sizes, kill food waste, and let your guests pay and leave at their own pace. An AI Sommelier sits inside every menu, ready to suggest dishes by mood, dietary need, group size, or occasion — guests speak, the menu listens.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/r/gilded-fork" data-testid="hero-try-menu" className="gold-btn rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2">
                Try a live menu <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              <Stat n="+34%" label="avg. check lift" />
              <Stat n="-22%" label="dish returns" />
              <Stat n="0s" label="to reprint" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
              <img src="https://images.pexels.com/photos/33432654/pexels-photo-33432654.jpeg" alt="Signature dish" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-white/80">
                  <PlayCircle className="w-4 h-4 text-[#E5A93C]" /> 5-sec loop
                </div>
                <h3 className="font-display text-3xl mt-2">Caviar Amuse-Bouche</h3>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full glass-light">Ordered by 58 today</span>
                  <span className="text-xs px-2 py-1 rounded-full glass-light">Chef's pick</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature grid */}
        <section className="max-w-7xl mx-auto mt-32 grid md:grid-cols-3 gap-6">
          <Feature icon={<ChefHat className="w-5 h-5" />} title="Visual-first menu" body="5-second video loops replace text. Guests crave before they order." />
          <Feature icon={<Sparkles className="w-5 h-5" />} title="In-menu AI Sommelier" body="Voice + text assistant suggests dishes by mood, dietary needs, group size or occasion — guests stop scrolling and start asking." />
          <Feature icon={<ChartLine className="w-5 h-5" />} title="Margin-aware sort" body="Stars rise. Dogs sink. Your highest-margin dishes win the top slot." />
        </section>

        {/* Demo restaurants */}
        <section className="max-w-7xl mx-auto mt-24 pb-24">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-3xl md:text-4xl">Live demo menus</h2>
            <span className="text-xs text-white/50 font-mono uppercase tracking-[0.2em]">tap to open</span>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {restaurants.map((r) => (
              <Link key={r.slug} to={`/r/${r.slug}`} data-testid={`demo-restaurant-${r.slug}`} className="group block relative h-64 rounded-2xl overflow-hidden border border-white/10">
                <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#E5A93C]">{r.cuisine}</div>
                  <div className="font-display text-3xl mt-1">{r.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-white/40 font-mono">
        MnuVerse — Restaurant Operating System · mnuverse.in
      </footer>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-l border-white/10 pl-3">
      <div className="font-display text-3xl gold-text">{n}</div>
      <div className="text-[11px] text-white/60 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="relative p-6 rounded-2xl border border-white/10 bg-[#121418]">
      <div className="w-10 h-10 rounded-full bg-[#E5A93C]/15 border border-[#E5A93C]/30 flex items-center justify-center text-[#E5A93C]">{icon}</div>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-white/60 leading-relaxed">{body}</p>
    </div>
  );
}
